/** @format */

import { BreakPointMinWidthValues, LoadingStrategy } from '@shared/data-access/enums';
import {
  IMAGE_CONFIG,
  IMAGE_LOADER,
  ImageLoaderConfig,
  NgOptimizedImage,
  isPlatformBrowser,
} from '@angular/common';
import {
  Component,
  PLATFORM_ID,
  computed,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ImageOptimizedSize } from './image-optimized-size.enum';
import { IsBotService } from '@shared/data-access/services';
import { MissingImageEventInfo } from '@shared/data-access/interfaces';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { Scene7ImageLoaderService } from '@shared/media/data-access';

@Component({
  selector: 'shared-media-component-optimized-image',
  templateUrl: './optimized-image.component.html',
  styleUrl: './optimized-image.component.scss',
  imports: [NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: IMAGE_LOADER,
      useFactory: (imageLoaderService: Scene7ImageLoaderService) => (config: ImageLoaderConfig) =>
        imageLoaderService.buildUrlByImageLoader(config),
      deps: [Scene7ImageLoaderService],
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        // Custom breakpoints based on T-shirt Size Breakpoints
        // https://homefield.dcsg.com/?path=/docs/resources-styles-breakpoints--docs
        breakpoints: [
          ImageOptimizedSize.THUMBNAIL_XS,
          ImageOptimizedSize.THUMBNAIL_M,
          BreakPointMinWidthValues.EXTRASMALL,
          BreakPointMinWidthValues.SMALL,
          ImageOptimizedSize.TABLET_XS,
          BreakPointMinWidthValues.MEDIUM,
          BreakPointMinWidthValues.LARGE,
          BreakPointMinWidthValues.EXTRALARGE,
          BreakPointMinWidthValues.EXTRAEXTRALARGE,
        ],
      },
    },
  ],
})
export class OptimizedImageComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly isBotService = inject(IsBotService);

  readonly imageLoaderConfig = input.required<ImageLoaderConfig>();
  readonly altText = input.required<string>();
  readonly isPriority = input(false);
  readonly loadingStrategyOverride = input(LoadingStrategy.Lazy);
  // Microdata fallback toggle. Default preserves legacy behavior; consumers
  // emitting JSON-LD structured data should pass `null` to suppress duplicate
  // microdata that Google would otherwise pick up alongside the JSON-LD.
  readonly imageItemProp = input<string | null>('image');

  readonly handleError = output<string>();
  readonly optimizedImageLoaded = output<Event>();

  readonly loadingStrategy = computed(() =>
    this.isPriority() ? LoadingStrategy.Eager : this.loadingStrategyOverride(),
  );

  /**
   * @description Posts to rumrunner when a missing product image is detected
   */
  handleImageError(requestedImg: string): void {
    if (
      isPlatformBrowser(this.platformId) &&
      !this.isBotService.isBot(window.navigator.userAgent)
    ) {
      const ecode = this.imageLoaderConfig().src;
      const color = this.imageLoaderConfig().loaderParams?.swatchColor?.slice(1);

      const eventPayload: MissingImageEventInfo = {
        pageUrl: window.location.href,
        requestedImg,
        productName: this.imageLoaderConfig().src,
      };

      if (ecode) {
        eventPayload.ecode = ecode;
      }

      if (color) {
        eventPayload.color = color;
      }

      this.handleError.emit(requestedImg);

      this.analyticsService.emitMissingImageEvent(eventPayload);
    }
  }
}
